import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BookingWidget = ({ tour }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      setIsSticky(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const totalPrice = (adults * tour?.pricePerAdult) + (children * tour?.pricePerChild);
  const serviceFee = totalPrice * 0.1;
  const finalPrice = totalPrice + serviceFee;

  const handleBookNow = () => {
    const bookingData = {
      tourId: tour?.id,
      tourName: tour?.name,
      date: selectedDate,
      adults,
      children,
      totalPrice: finalPrice
    };
    
    localStorage.setItem('bookingData', JSON.stringify(bookingData));
    navigate('/booking-flow');
  };

  const incrementGuests = (type) => {
    if (type === 'adults' && adults < tour?.maxGroupSize) {
      setAdults(adults + 1);
    } else if (type === 'children' && children < tour?.maxGroupSize - adults) {
      setChildren(children + 1);
    }
  };

  const decrementGuests = (type) => {
    if (type === 'adults' && adults > 1) {
      setAdults(adults - 1);
    } else if (type === 'children' && children > 0) {
      setChildren(children - 1);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.split('T')?.[0];
  };

  return (
    <div className={`bg-white border border-border rounded-xl p-6 transition-all duration-300 ${
      isSticky ? 'fixed top-24 right-6 z-40 shadow-2xl w-80' : 'sticky top-24'
    }`}>
      {/* Price Header */}
      <div className="mb-6">
        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-bold text-foreground">${tour?.pricePerAdult}</span>
          <span className="text-muted-foreground">per adult</span>
        </div>
        {tour?.originalPrice && (
          <div className="flex items-center space-x-2 mt-1">
            <span className="text-sm text-muted-foreground line-through">${tour?.originalPrice}</span>
            <span className="text-sm bg-success/10 text-success px-2 py-1 rounded-full">
              Save {Math.round(((tour?.originalPrice - tour?.pricePerAdult) / tour?.originalPrice) * 100)}%
            </span>
          </div>
        )}
      </div>
      {/* Date Selection */}
      <div className="mb-4">
        <Input
          type="date"
          label="Select Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e?.target?.value)}
          min={getMinDate()}
          required
        />
      </div>
      {/* Guest Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-3">Guests</label>
        
        {/* Adults */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium">Adults</div>
            <div className="text-sm text-muted-foreground">Age 13+</div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => decrementGuests('adults')}
              disabled={adults <= 1}
              className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Minus" size={16} />
            </button>
            <span className="w-8 text-center font-medium">{adults}</span>
            <button
              onClick={() => incrementGuests('adults')}
              disabled={adults >= tour?.maxGroupSize}
              className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Plus" size={16} />
            </button>
          </div>
        </div>

        {/* Children */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Children</div>
            <div className="text-sm text-muted-foreground">Age 2-12</div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => decrementGuests('children')}
              disabled={children <= 0}
              className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Minus" size={16} />
            </button>
            <span className="w-8 text-center font-medium">{children}</span>
            <button
              onClick={() => incrementGuests('children')}
              disabled={children + adults >= tour?.maxGroupSize}
              className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Plus" size={16} />
            </button>
          </div>
        </div>
      </div>
      {/* Price Breakdown */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>${tour?.pricePerAdult} x {adults} adults</span>
            <span>${adults * tour?.pricePerAdult}</span>
          </div>
          {children > 0 && (
            <div className="flex justify-between text-sm">
              <span>${tour?.pricePerChild} x {children} children</span>
              <span>${children * tour?.pricePerChild}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span>Service fee</span>
            <span>${serviceFee?.toFixed(2)}</span>
          </div>
          <div className="border-t border-border pt-2 mt-2">
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span>${finalPrice?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Booking Button */}
      <Button
        onClick={handleBookNow}
        disabled={!selectedDate}
        fullWidth
        className="mb-4"
        iconName="Calendar"
        iconPosition="left"
      >
        Book Now
      </Button>
      {/* Additional Info */}
      <div className="space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={16} />
          <span>Free cancellation up to 24 hours</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} />
          <span>Instant confirmation</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={16} />
          <span>Small group experience</span>
        </div>
      </div>
      {/* Recent Bookings */}
      <div className="mt-4 p-3 bg-accent/10 rounded-lg">
        <div className="flex items-center space-x-2 text-sm">
          <Icon name="TrendingUp" size={16} className="text-accent" />
          <span className="text-accent font-medium">12 people booked this tour today</span>
        </div>
      </div>
    </div>
  );
};

export default BookingWidget;